import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BidPerfFilterPanel from "./BidPerfFilterPanel";
import {
  CASE_TYPE_CONFIG,
  CASE_TYPES,
  MATCH_TYPE_BID_TOOLTIP,
  MATCH_TYPE_KEYS,
  MATCH_TYPE_LABELS,
} from "../lib/constants";
import { filterModuleHasContent } from "../lib/cubeFilters";
import {
  BID_V2_COLORS,
  collapseHeaderBtnSx,
  fieldLabelSx,
  numberFieldInputProps,
  radioLabelSx,
  textFieldSx,
} from "../lib/bidAutomationStyles";

function BidInfoTooltip({ title }) {
  if (!title) return null;
  return (
    <Tooltip title={title} arrow placement="top">
      <InfoOutlinedIcon
        sx={{ fontSize: 16, color: BID_V2_COLORS.muted, cursor: "help", verticalAlign: "middle" }}
        aria-label="More information"
      />
    </Tooltip>
  );
}

function CaseCountBadge({ count, max, color }) {
  return (
    <Box
      component="span"
      sx={{
        ml: 0.75,
        px: 0.75,
        py: 0.15,
        borderRadius: 1,
        fontSize: "0.6875rem",
        fontWeight: 700,
        color: "#fff",
        bgcolor: count > 0 ? color : "#94A3B8",
        lineHeight: 1.4,
      }}
    >
      {count}/{max}
    </Box>
  );
}

function MatchTypeCard({
  matchKey,
  label,
  enabled,
  value,
  fallbackPct,
  onToggle,
  onValueChange,
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        flex: 1,
        minWidth: 140,
        borderColor: enabled ? "primary.main" : BID_V2_COLORS.border,
        bgcolor: enabled ? "rgba(97, 72, 246, 0.04)" : "#fff",
      }}
    >
      <FormControlLabel
        control={<Checkbox size="small" checked={enabled} onChange={onToggle} />}
        label={<Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>{label}</Typography>}
        sx={{ mb: enabled ? 1 : 0, ml: 0 }}
      />
      {enabled ? (
        <TextField
          size="small"
          type="number"
          fullWidth
          value={value}
          onChange={onValueChange}
          inputProps={{ min: 0, ...numberFieldInputProps }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "text.secondary" }}>
                  %
                </Typography>
              </InputAdornment>
            ),
          }}
          sx={textFieldSx}
        />
      ) : (
        <Typography sx={{ fontSize: "0.75rem", color: BID_V2_COLORS.muted, fontWeight: 500, pl: 0.5 }}>
          Uses case bid change {fallbackPct !== "" && fallbackPct != null ? `+${fallbackPct}%` : "%"}
        </Typography>
      )}
    </Paper>
  );
}

function CaseCard({
  caseType,
  caseItem,
  caseIndex,
  canDelete,
  expandedPerfCaseId,
  onTogglePerf,
  onUpdateCase,
  onRemoveCase,
  onFilterOutput,
  onTimePeriodChange,
  nonNegativeValue,
}) {
  const config = CASE_TYPE_CONFIG[caseType];
  const isPause = caseType === CASE_TYPES.PAUSE;
  const isPerfOpen = expandedPerfCaseId === caseItem.localId;
  const mt = caseItem.matchTypeBids ?? {};
  const filtersApplied = filterModuleHasContent(caseItem.perfFilter?.filterModuleFilters);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 1.5,
        borderRadius: 2,
        borderColor: BID_V2_COLORS.border,
        bgcolor: BID_V2_COLORS.slotBg,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: BID_V2_COLORS.title }}>
          {config.label} {caseIndex + 1}
        </Typography>
        <IconButton
          size="small"
          disabled={!canDelete}
          aria-label={`Remove ${config.label} ${caseIndex + 1}`}
          onClick={() => onRemoveCase(caseItem.localId)}
          sx={{ color: canDelete ? "error.main" : BID_V2_COLORS.muted }}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Button
        fullWidth
        endIcon={isPerfOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        onClick={() => onTogglePerf(caseItem.localId)}
        aria-expanded={isPerfOpen}
        sx={{
          ...collapseHeaderBtnSx,
          mt: 0,
          mb: isPerfOpen ? 0 : 0,
          ...(filtersApplied &&
            !isPerfOpen && {
              borderColor: "primary.main",
              color: "primary.main",
            }),
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, textAlign: "left" }}>
          <span>Performance filters</span>
          {filtersApplied && !isPerfOpen ? (
            <Typography
              component="span"
              sx={{ fontSize: "0.75rem", fontWeight: 600, color: "primary.main" }}
            >
              Applied
            </Typography>
          ) : null}
        </Box>
      </Button>

      <BidPerfFilterPanel
        open={isPerfOpen}
        slotLocalId={caseItem.localId}
        filterModuleFilters={caseItem.perfFilter?.filterModuleFilters}
        onOutput={onFilterOutput}
        timePeriod={caseItem.perfFilter?.timePeriod ?? "LAST_7_DAY"}
        onTimePeriodChange={onTimePeriodChange}
        isFilterApplied={filtersApplied}
        onAppliedCallback={() => {}}
      />

      {!isPause ? (
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Box>
            <Typography sx={fieldLabelSx}>{config.percentageLabel}</Typography>
            <TextField
              size="small"
              type="number"
              value={caseItem.percentageChange ?? ""}
              onChange={(e) =>
                onUpdateCase(caseItem.localId, {
                  percentageChange: nonNegativeValue(e.target.value),
                })
              }
              sx={{ ...textFieldSx, width: 160, display: "block" }}
              inputProps={{ min: 0, ...numberFieldInputProps }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "text.secondary" }}>
                      %
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={Boolean(mt.enabled)}
                onChange={(e) =>
                  onUpdateCase(caseItem.localId, {
                    matchTypeBids: {
                      ...mt,
                      enabled: e.target.checked,
                    },
                  })
                }
              />
            }
            label={
              <Stack direction="row" alignItems="center" spacing={0.5} component="span">
                <span>{config.matchTypeLabel}</span>
                <BidInfoTooltip title={MATCH_TYPE_BID_TOOLTIP} />
              </Stack>
            }
            sx={{ ...radioLabelSx, alignItems: "center", ml: 0 }}
          />

          {mt.enabled ? (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              {MATCH_TYPE_KEYS.map((key) => (
                <MatchTypeCard
                  key={key}
                  matchKey={key}
                  label={MATCH_TYPE_LABELS[key]}
                  enabled={Boolean(mt[key]?.enabled)}
                  value={mt[key]?.value ?? ""}
                  fallbackPct={caseItem.percentageChange}
                  onToggle={(e) =>
                    onUpdateCase(caseItem.localId, {
                      matchTypeBids: {
                        ...mt,
                        [key]: {
                          ...(mt[key] ?? { value: "" }),
                          enabled: e.target.checked,
                          value: e.target.checked
                            ? mt[key]?.value !== "" && mt[key]?.value != null
                              ? mt[key].value
                              : "25"
                            : mt[key]?.value ?? "",
                        },
                      },
                    })
                  }
                  onValueChange={(e) =>
                    onUpdateCase(caseItem.localId, {
                      matchTypeBids: {
                        ...mt,
                        [key]: {
                          ...(mt[key] ?? { enabled: true }),
                          enabled: true,
                          value: nonNegativeValue(e.target.value),
                        },
                      },
                    })
                  }
                />
              ))}
            </Stack>
          ) : null}
        </Stack>
      ) : (
        <Typography sx={{ mt: 1.5, fontSize: "0.8125rem", color: BID_V2_COLORS.muted }}>
          Matching targetings will be paused when this case’s performance filters are met.
        </Typography>
      )}
    </Paper>
  );
}

export default function BidSlotCasesPanel({
  slot,
  expandedPerfCaseId,
  setExpandedPerfCaseId,
  onUpdateSlot,
  onUpdateCase,
  onAddCase,
  onRemoveCase,
  onCaseFilterOutput,
  onCaseTimePeriodChange,
  nonNegativeValue,
}) {
  const activeTab = slot.activeCaseTab || CASE_TYPES.INCREASE;
  const casesForTab = slot.cases?.[activeTab] ?? [];
  const config = CASE_TYPE_CONFIG[activeTab];
  const canAdd = casesForTab.length < config.maxRules;

  const handleAddCase = () => {
    const newCaseId = onAddCase?.(activeTab);
    if (newCaseId) {
      setExpandedPerfCaseId(newCaseId);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500, color: BID_V2_COLORS.muted, mb: 1 }}>
        The bid change applies to every targeting in all selected campaigns for this slot.
      </Typography>
      <Typography
        sx={{
          fontSize: "0.75rem",
          fontWeight: 700,
          letterSpacing: "0.04em",
          color: BID_V2_COLORS.label,
          mb: 1,
        }}
      >
        RULES FOR THIS SLOT
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(_, next) => onUpdateSlot({ activeCaseTab: next })}
        sx={{
          mb: 2,
          minHeight: 42,
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
            minHeight: 42,
            fontSize: "0.875rem",
          },
        }}
      >
        {Object.values(CASE_TYPES).map((caseType) => {
          const cfg = CASE_TYPE_CONFIG[caseType];
          const count = slot.cases?.[caseType]?.length ?? 0;
          return (
            <Tab
              key={caseType}
              value={caseType}
              label={
                <Stack direction="row" alignItems="center" component="span">
                  <span>{cfg.label}</span>
                  <CaseCountBadge count={count} max={cfg.maxRules} color={cfg.badgeColor} />
                </Stack>
              }
            />
          );
        })}
      </Tabs>

      {casesForTab.map((caseItem, index) => (
        <CaseCard
          key={caseItem.localId}
          caseType={activeTab}
          caseItem={caseItem}
          caseIndex={index}
          canDelete={true}
          expandedPerfCaseId={expandedPerfCaseId}
          onTogglePerf={(id) =>
            setExpandedPerfCaseId((prev) => (prev === id ? null : id))
          }
          onUpdateCase={(caseId, patch) => onUpdateCase(activeTab, caseId, patch)}
          onRemoveCase={(caseId) => onRemoveCase(activeTab, caseId)}
          onFilterOutput={onCaseFilterOutput(caseItem.localId)}
          onTimePeriodChange={(tp) => onCaseTimePeriodChange(caseItem.localId, tp)}
          nonNegativeValue={nonNegativeValue}
        />
      ))}

      {canAdd ? (
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={handleAddCase}
          sx={{
            mt: 0.5,
            textTransform: "none",
            fontWeight: 600,
            borderColor: BID_V2_COLORS.border,
            color: BID_V2_COLORS.title,
          }}
        >
          Add {config.label}
        </Button>
      ) : null}
    </Box>
  );
}
